import React, { useState } from 'react';
import { ArrowLeft, Lock, CalendarDays, Shield, TrendingUp, Globe, Layers, Mail, User, Building2, Phone, MessageSquare } from 'lucide-react';
import { getApiBase } from '../lib/apiBase';

const API = getApiBase();

const InvestorTeaserPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [showAccess, setShowAccess] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await fetch(`${API}/api/forms/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'investor',
          name: form.name,
          email: form.email,
          phone: form.phone,
          company: form.company,
          message: form.message,
          extra: { source: 'investor-teaser' },
        }),
      });
      if (!res.ok) throw new Error(`Submit failed (${res.status})`);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccessSubmit = (e) => {
    e.preventDefault();
    if (accessCode === 'SAGINVESTOR2026') {
      localStorage.setItem('sag_investor_access', 'granted');
      window.location.href = '/investor-portal';
    } else {
      setAccessError('Invalid access code');
      setTimeout(() => setAccessError(''), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="investor-teaser-page">
      {/* Header */}
      <header className="bg-black border-b border-white/10 py-4 px-4 md:px-8">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors" data-testid="teaser-back-home">
            <ArrowLeft className="w-4 h-4" />
            <span className="tracking-wide hidden sm:inline">Back to Home</span>
          </a>
          <img src="/logos/sag-logo.png" alt="SAG" className="h-7 w-auto" />
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 md:py-32 px-4 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-16 bg-[#C9A227]/10 flex items-center justify-center mx-auto mb-8">
            <TrendingUp className="w-8 h-8 text-[#C9A227]" />
          </div>

          <p className="text-[#C9A227] text-[10px] md:text-xs tracking-[0.4em] uppercase mb-6">
            Investor Relations
          </p>

          <h1 className="text-black text-4xl sm:text-5xl md:text-6xl font-light leading-[1.1] mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
            Building the Future of
            <br />
            <span className="text-[#C9A227]">Caribbean Fintech</span>
          </h1>

          <div className="w-16 h-[2px] bg-[#C9A227] mx-auto mb-8" />

          <p className="text-gray-500 text-base md:text-lg font-light max-w-2xl mx-auto mb-12 leading-relaxed">
            Sebastian Alliance Group is building an integrated ecosystem of digital platforms
            powering payments, commerce, and financial infrastructure across the Caribbean and beyond.
            Investor access is available by request to qualified investors only.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {[
              { value: '15+', label: 'Platforms & Solutions' },
              { value: 'Global', label: 'Payment Integrations' },
              { value: '3+', label: 'Regional Markets' },
              { value: '2010', label: 'Founded' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl md:text-3xl font-light text-[#1e3a5f] mb-1">{s.value}</div>
                <div className="text-gray-400 text-xs uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Highlights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 text-left">
            {[
              { icon: Layers, title: 'Integrated Ecosystem', desc: 'Payments, commerce, real estate, and automation — all connected under one umbrella.' },
              { icon: Globe, title: 'Multi-Market Reach', desc: 'Caribbean, United States, and expanding into LATAM. Serving underbanked markets.' },
              { icon: Shield, title: 'Strategic Partners', desc: 'Scotiabank, Visa, NACHA, SWIFT, Fiserv — enterprise-grade partnerships.' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="p-6 border border-gray-200 bg-gray-50">
                  <Icon className="w-6 h-6 text-[#C9A227] mb-4" />
                  <h3 className="text-[#1e3a5f] text-sm font-medium uppercase tracking-wider mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={() => { setShowForm(true); setShowAccess(false); }}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#C9A227] text-black text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#1e3a5f] hover:text-white transition-colors"
              data-testid="request-access-btn"
            >
              <CalendarDays className="w-4 h-4" />
              Request Investor Access
            </button>
            <button
              onClick={() => { setShowAccess(true); setShowForm(false); }}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#1e3a5f] text-[#1e3a5f] text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#1e3a5f] hover:text-white transition-colors"
              data-testid="investor-login-btn"
            >
              <Lock className="w-4 h-4" />
              Investor Login
            </button>
          </div>

          <p className="text-gray-400 text-xs tracking-wide">
            Qualified investors only. Full materials available upon request.
          </p>
        </div>
      </section>

      {/* Modal */}
      {(showForm || showAccess) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => { setShowForm(false); setShowAccess(false); setSubmitted(false); }}>
          <div className="bg-white w-full max-w-md p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setShowForm(false); setShowAccess(false); setSubmitted(false); }} className="absolute top-4 right-4 text-gray-400 hover:text-black text-lg" data-testid="modal-close">&times;</button>

            {showAccess && (
              <>
                <div className="flex items-center gap-2 mb-6">
                  <Lock className="w-4 h-4 text-[#C9A227]" />
                  <h3 className="text-sm font-medium text-black uppercase tracking-wider">Investor Portal Access</h3>
                </div>
                <p className="text-gray-500 text-xs mb-6">Enter your investor access code to view the full portal.</p>
                <form onSubmit={handleAccessSubmit} className="space-y-4">
                  <input
                    type="password"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    placeholder="Enter access code"
                    className="w-full px-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227] text-center tracking-wider"
                    autoFocus
                    data-testid="investor-access-input"
                  />
                  {accessError && <p className="text-red-500 text-xs text-center">{accessError}</p>}
                  <button type="submit" className="w-full py-3 bg-[#1e3a5f] text-white text-xs tracking-wider uppercase font-medium hover:bg-black transition-colors" data-testid="investor-access-submit">
                    Access Portal
                  </button>
                  <p className="text-center text-xs text-gray-400">
                    Don't have a code? <button type="button" onClick={() => { setShowAccess(false); setShowForm(true); }} className="text-[#C9A227] hover:underline">Request Access</button>
                  </p>
                </form>
              </>
            )}

            {showForm && !submitted && (
              <>
                <div className="flex items-center gap-2 mb-6">
                  <CalendarDays className="w-4 h-4 text-[#C9A227]" />
                  <h3 className="text-sm font-medium text-black uppercase tracking-wider">Request Investor Access</h3>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Name *</label>
                      <input type="text" required value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className="w-full px-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]" data-testid="req-name" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Company</label>
                      <input type="text" value={form.company} onChange={(e) => setForm(p => ({ ...p, company: e.target.value }))} className="w-full px-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]" data-testid="req-company" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Email *</label>
                    <input type="email" required value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} placeholder="investor@company.com" className="w-full px-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]" data-testid="req-email" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Phone</label>
                    <input type="tel" value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} className="w-full px-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]" data-testid="req-phone" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Message</label>
                    <textarea rows={3} value={form.message} onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))} placeholder="Tell us about your investment interest..." className="w-full px-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227] resize-none" data-testid="req-message" />
                  </div>
                  <button type="submit" disabled={submitting} className="w-full py-3 bg-[#C9A227] text-black text-xs tracking-wider uppercase font-medium hover:bg-[#b8911f] transition-colors disabled:opacity-60 disabled:cursor-not-allowed" data-testid="req-submit">
                    {submitting ? 'Submitting…' : 'Submit Request'}
                  </button>
                  {submitError && <p className="text-center text-red-600 text-xs" data-testid="req-error">{submitError}</p>}
                </form>
              </>
            )}

            {showForm && submitted && (
              <div className="text-center py-6">
                <div className="w-14 h-14 bg-[#C9A227]/10 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-7 h-7 text-[#C9A227]" />
                </div>
                <h3 className="text-lg font-medium text-black mb-2">Request Submitted</h3>
                <p className="text-gray-500 text-sm mb-4">Our investor relations team will contact you within 24-48 hours.</p>
                <p className="text-xs text-gray-400">For immediate assistance: <a href="mailto:info@sebasalliance.com" className="text-[#C9A227]">info@sebasalliance.com</a></p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-[#1e3a5f] py-8 px-4 text-center">
        <p className="text-white/40 text-[10px] tracking-wider">
          This material is for informational purposes only and does not constitute an offer to sell securities.
        </p>
      </footer>
    </div>
  );
};

export default InvestorTeaserPage;
